import type {
	TopologyFaceRegionBoundary,
	TopologyFaceRegionBoundaryFace,
	TopologyFaceRegionBoundaryResolverInput,
	TopologyFaceRegionBoundaryResolverResult,
} from '../../../../../types/reference/sketch-entity-reference/TopologyFaceRegionBoundaryResolver';

/**
 * Convert Entities 相邻面区域外边界解析器。
 *
 * 本类只处理纯数据：同一 owner 内，共享稳定边的面属于同一连通区域；
 * 在单个区域中出现多次的边是内部边，只保留唯一出现的外边界。
 */
export class TopologyFaceRegionBoundaryResolver {
	/**
	 * 把一组面划分为连通区域并解析每个区域的外边界。
	 *
	 * @param input 面、owner 作用域与稳定边键。
	 * @returns 按输入顺序排列的区域及其外边界。
	 */
	public resolve<TFace, TEdge>(
		input: TopologyFaceRegionBoundaryResolverInput<TFace, TEdge>,
	): TopologyFaceRegionBoundaryResolverResult<TFace, TEdge> {
		const visitedFaceIndices = new Set<number>();
		const regions: TopologyFaceRegionBoundary<TFace, TEdge>[] = [];

		input.faces.forEach((face, faceIndex) => {
			if (visitedFaceIndices.has(faceIndex)) {
				return;
			}
			const componentIndices = this.collectComponentIndices(
				input.faces,
				faceIndex,
				visitedFaceIndices,
			);
			const componentFaces = componentIndices
				.map(index => input.faces[index])
				.filter((candidate): candidate is TopologyFaceRegionBoundaryFace<TFace, TEdge> => (
					Boolean(candidate)
				));
			regions.push({
				ownerKey: face.ownerKey,
				faces: componentFaces.map(candidate => candidate.face),
				boundaryEdges: this.collectBoundaryEdges(componentFaces),
			});
		});

		return {regions};
	}

	/**
	 * 收集与起始面通过共享边相连的同 owner 面索引。
	 *
	 * @param faces 全部候选面。
	 * @param startIndex 起始面索引。
	 * @param visitedFaceIndices 已归属其它区域的面索引。
	 * @returns 当前连通分量的面索引。
	 */
	private collectComponentIndices<TFace, TEdge>(
		faces: readonly TopologyFaceRegionBoundaryFace<TFace, TEdge>[],
		startIndex: number,
		visitedFaceIndices: Set<number>,
	): number[] {
		const componentIndices: number[] = [];
		const pendingIndices = [startIndex];
		visitedFaceIndices.add(startIndex);

		while (pendingIndices.length > 0) {
			const currentIndex = pendingIndices.shift();
			const currentFace = currentIndex === undefined ? undefined : faces[currentIndex];
			if (currentIndex === undefined || !currentFace) {
				continue;
			}
			componentIndices.push(currentIndex);
			const currentEdgeKeys = new Set(currentFace.edges.map(edge => edge.key));
			faces.forEach((candidate, candidateIndex) => {
				if (
					visitedFaceIndices.has(candidateIndex)
					|| candidate.ownerKey !== currentFace.ownerKey
				) {
					return;
				}
				if (candidate.edges.some(edge => currentEdgeKeys.has(edge.key))) {
					visitedFaceIndices.add(candidateIndex);
					pendingIndices.push(candidateIndex);
				}
			});
		}
		return componentIndices.sort((left, right) => left - right);
	}

	/**
	 * 收集只被当前区域一个面贡献的边。
	 *
	 * @param faces 单个连通区域中的面。
	 * @returns 保留原面、原边顺序的区域外边界。
	 */
	private collectBoundaryEdges<TFace, TEdge>(
		faces: readonly TopologyFaceRegionBoundaryFace<TFace, TEdge>[],
	): Array<{face: TFace; key: string; edge: TEdge}> {
		const occurrenceCounts = new Map<string, number>();
		faces.forEach((face) => {
			const faceEdgeKeys = new Set(face.edges.map(edge => edge.key));
			faceEdgeKeys.forEach((key) => {
				occurrenceCounts.set(key, (occurrenceCounts.get(key) ?? 0) + 1);
			});
		});

		return faces.flatMap((face) => {
			const emittedKeys = new Set<string>();
			return face.edges.flatMap((edge) => {
				if (emittedKeys.has(edge.key) || occurrenceCounts.get(edge.key) !== 1) {
					return [];
				}
				emittedKeys.add(edge.key);
				return [{face: face.face, key: edge.key, edge: edge.edge}];
			});
		});
	}
}
